import { useEffect, useMemo, useState } from "react";
import api, { apiError } from "@/lib/api";
import { PageHeader } from "@/components/StatCard";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Disc3 } from "lucide-react";

const TABS = [["", "All"], ["mortgage", "Mortgage"], ["referral", "Referrals"], ["pm", "Property management"], ["surety", "Surety bonds"]];
const when = (s) => (s ? new Date(s).toLocaleString([], { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" }) : "");
const mins = (ms) => { const s = Math.round((ms || 0) / 1000); return s ? `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}` : ""; };
const nice = (s) => { const t = (s || "").replace(/_/g, " ").replace(/\bmlo\b/gi, "MLO"); return t.charAt(0).toUpperCase() + t.slice(1); };

const Rec = ({ r }) => (
  <div className="border-t border-slate-100 py-4" data-testid={`recording-${r.id}`}>
    <div className="flex flex-wrap items-center gap-2">
      <span className="font-semibold text-slate-900">{r.name}</span>
      {r.phone && !r.name.includes(r.phone) && <span className="text-xs text-slate-500">{r.phone}</span>}
      <Badge variant="outline" className="text-[10px]">{r.source_label}</Badge>
      {r.direction === "inbound" && <Badge variant="outline" className="text-[10px]">Inbound</Badge>}
      {r.outcome && <Badge className="border-0 bg-slate-100 text-slate-700" data-testid={`recording-outcome-${r.id}`}>{nice(r.outcome)}</Badge>}
    </div>
    <div className="mt-0.5 text-xs text-slate-500">{when(r.created_at)}{r.duration_ms ? ` · ${mins(r.duration_ms)}` : ""}{r.owner ? ` · ${r.owner}` : ""}</div>
    <p className="mt-2 text-sm text-slate-700" data-testid={`recording-summary-${r.id}`}>{r.summary || "No summary for this call."}</p>
    {r.recording_url
      ? <audio controls preload="none" src={r.recording_url} className="mt-2 h-9 w-full max-w-xl" data-testid={`recording-audio-${r.id}`} />
      : <p className="mt-1 text-xs text-slate-400">No recording for this call.</p>}
  </div>
);

export default function AdminRecordings({ endpoint = "/admin/call-recordings", tabs = true, subtitle = "Every recorded Ava call, with its AI summary." }) {
  const [rows, setRows] = useState(null);
  const [src, setSrc] = useState("");
  const [q, setQ] = useState("");
  useEffect(() => {
    setRows(null);
    api.get(endpoint, { params: src ? { source: src } : {} }).then((r) => setRows(r.data.recordings)).catch((e) => toast.error(apiError(e)));
  }, [src, endpoint]);
  const shown = useMemo(() => (rows || []).filter((r) => !q || `${r.name} ${r.phone} ${r.summary} ${r.owner}`.toLowerCase().includes(q.toLowerCase())), [rows, q]);
  return (
    <div data-testid="admin-recordings-page">
      <PageHeader title="AI Call Recordings" subtitle={subtitle} />
      <Card className="p-6">
        <div className="mb-2 flex flex-wrap items-center justify-between gap-3">
          {tabs ? <div className="flex max-w-full flex-wrap gap-1">
            {TABS.map(([k, l]) => (
              <button key={k || "all"} onClick={() => setSrc(k)} data-testid={`recordings-tab-${k || "all"}`}
                className={`whitespace-nowrap rounded-full px-3 py-1.5 text-sm font-medium transition-colors ${src === k ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`}>{l}</button>
            ))}
          </div> : <span />}
          <Input className="max-w-xs" placeholder="Search name, phone or summary" value={q} onChange={(e) => setQ(e.target.value)} data-testid="recordings-search" />
        </div>
        {rows === null && <p className="py-6 text-center text-sm text-slate-500">Loading…</p>}
        {rows && shown.map((r) => <Rec key={`${r.source}-${r.id}`} r={r} />)}
        {rows && !shown.length && <div className="flex flex-col items-center gap-2 py-10 text-slate-400" data-testid="recordings-empty"><Disc3 className="h-8 w-8" /><p className="text-sm">No recordings yet.</p></div>}
      </Card>
    </div>
  );
}
